import { useCallback } from 'react';

import { atom, type PrimitiveAtom, useAtom } from 'jotai';

import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls/web-storage-service';

export type LabsColorMode = 'light' | 'dark';

export const LABS_COLOR_MODE_STORAGE_KEY = 'labs-color-mode';

const getInitialColorMode = (): LabsColorMode => {
	try {
		return AtlBrowserStorageLocal.getItem(LABS_COLOR_MODE_STORAGE_KEY) === 'light'
			? 'light'
			: 'dark';
	} catch {
		return 'dark';
	}
};

export const LabsColorModeAtom: PrimitiveAtom<LabsColorMode> =
	atom<LabsColorMode>(getInitialColorMode());

export const useLabsColorMode = (): readonly [
	LabsColorMode,
	(colorMode: LabsColorMode) => void,
] => {
	const [colorMode, setColorMode] = useAtom(LabsColorModeAtom);
	const updateColorMode = useCallback(
		(nextColorMode: LabsColorMode) => {
			setColorMode(nextColorMode);
			try {
				void Promise.resolve(
					AtlBrowserStorageLocal.setItem(LABS_COLOR_MODE_STORAGE_KEY, nextColorMode),
				).catch(() => undefined);
			} catch {
				// The in-memory selection remains active when browser storage is unavailable.
			}
		},
		[setColorMode],
	);

	return [colorMode, updateColorMode] as const;
};
